import { useState } from 'react';
import { isAfter } from 'date-fns';
import { getBidWinnerDialogKey } from '@tempmee/awards';
import { BidTrophy, Column, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { BestInDentalV2 } from '~/constants/ff.posthog';
import { useBidWinnerRecognition } from '~/domain/BestInDental';
import { parseDateString } from '~/utils/DateUtils';
import { BID_DIALOG_STYLE, WinnersToolkitButton } from '../shared';

const WINNER_DESCRIPTION =
  "This is recognition for consistently delivering outstanding patient care, a positive in-office culture, and a great experience for the GoTu professionals who work with you. Consistency and respect like this don't go unnoticed. You've earned it!";

export const BidWinnerBanner = () => {
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  // Gate the history fetch on the flag — this banner mounts on the dashboard
  // for every office, so an ungated query would hit awards-service on every
  // dashboard load even with BID off.
  const { currentCycleWin, isLoading } = useBidWinnerRecognition(isFlagOn);
  const user = useUserSelector();
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  if (!isFlagOn || isLoading || !currentCycleWin || !user?.id) {
    return null;
  }

  const cycleEndDate = parseDateString(currentCycleWin.cycleEndDate);

  if (isAfter(new Date(), cycleEndDate)) {
    return null;
  }

  const storageKey = getBidWinnerDialogKey(user.id, currentCycleWin.cycleId);

  if (dismissedKey === storageKey || localStorage.getItem(storageKey) === 'true') {
    return null;
  }

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissedKey(storageKey);
  };

  return (
    <Dialog open onClose={dismiss} size="small" style={BID_DIALOG_STYLE} data-cy="bid-winner-banner">
      <DialogTitle onClose={dismiss} className="justify-end" />
      <DialogContent>
        <Column align="center" gap={4}>
          <BidTrophy size="lg" data-cy="bid-trophy-badge" />
          {/* Year-based recognition ("2026 Winner") per the v2 design. The
              server populates cycleYear (awards-service#35) but it stays
              optional in the contract, so render this line only when present;
              the year is never parsed out of the cycle name. */}
          {currentCycleWin.cycleYear != null && (
            <Typography
              variant="subtitle2"
              bold
              color={colors.text.primary}
              textAlign="center"
              data-cy="bid-winner-year"
            >
              {currentCycleWin.cycleYear} Winner
            </Typography>
          )}
          <Typography variant="subtitle1" bold color={colors.text.primary} textAlign="center">
            You&apos;re One of Our Best in Dental
          </Typography>
          <Typography color={colors.text.secondary} textAlign="center">
            {WINNER_DESCRIPTION}
          </Typography>
        </Column>
      </DialogContent>
      <DialogActions className="justify-center">
        <WinnersToolkitButton />
      </DialogActions>
    </Dialog>
  );
};
